import { Logger, UserManager } from "../";
import fakeNames from "./fakeNames.json";

const DEFAULT_MISSING_PLAYER_NAME_INFO: PlayerNameInfo = {
  fullName: "Undefined User",
  shortName: "UUser",
  displayName: "UUser",
};

export type PlayerNameInfo = {
  shortName: string;
  fullName: string;
  displayName: string;
};

export function getCurrentPlayerNameInfo(): PlayerNameInfo {
  const session = UserManager.instance.user;
  const entity = session?.type === "teacher" ? session.teacher : session?.student;
  return getPlayerNameInfo(session?.type, entity, "getCurrentPlayerNameInfo");
}

export function getPlayerNameInfo(
  entityType: "student" | "teacher" | undefined,
  entity: { _id?: string; entityId?: string; title?: string; firstName?: string; lastName?: string } | undefined,
  caller = ""
): PlayerNameInfo {
  if (!entity) {
    const stack = new Error().stack;
    Logger.logWarn(`[getPlayerNameInfo] entity is undefined for entityType=[${entityType}] caller=[${caller}]`, {
      stack,
    });
    return DEFAULT_MISSING_PLAYER_NAME_INFO;
  }

  const { title } = entity;
  const { firstName, lastName } = getFirstLastName(entity);
  const firstLastName = `${firstName} ${lastName}`.trim();
  const fullName = entityType === "teacher" ? `${title || ""} ${firstLastName}`.trim() : firstLastName;
  const displayName = getDisplayName(entityType, title, firstName, lastName);
  return {
    fullName,
    shortName: firstName || displayName,
    displayName,
  };
}

export function getPlayerDisplayName(
  entityType: "student" | "teacher",
  entity: { _id?: string; entityId?: string; title?: string; firstName?: string; lastName?: string } | undefined
): string {
  return getPlayerNameInfo(entityType, entity, "getPlayerDisplayName").displayName;
}

function getFirstLastName(entity: { _id?: string; entityId?: string; firstName?: string; lastName?: string }) {
  let firstName = (entity.firstName ?? "").trim();
  let lastName = (entity.lastName ?? "").trim();

  // If the user doesn't have a first or last name, use an adjective/noun to create a fake one for them
  if (firstName === "" && lastName === "") {
    Logger.logWarn("[getFirstLastName] Both firstName and lastName are missing", {
      _id: entity._id || entity.entityId,
      firstName,
      lastName,
    });

    const fakeName = getFakeName(entity._id || entity.entityId);
    firstName = fakeName.firstName;
    lastName = fakeName.lastName;
  }

  firstName = capitalizeFirstLetter(firstName);
  lastName = capitalizeFirstLetter(lastName);

  return { firstName, lastName };
}

function hashFn(str: string) {
  let i = str.length;
  let hash1 = 5381;
  let hash2 = 52711;

  while (i--) {
    const char = str.charCodeAt(i);
    hash1 = (hash1 * 33) ^ char;
    hash2 = (hash2 * 33) ^ char;
  }

  return (hash1 >>> 0) * 4096 + (hash2 >>> 0);
}

function getFakeName(id: string | undefined) {
  let hash;
  if (id) {
    //Index off of their ID to stay consistent in future sessions
    //Grab the first 12 digits of ID to ensure the number isn't crazy large
    const firstPartID = id.substring(0, Math.min(12, id.length - 1));
    hash = hashFn(firstPartID);
  } else {
    hash = Math.round(Math.random() * 100);
  }

  //Index into the fake names
  const firstName = fakeNames.firstNames[hash % fakeNames.firstNames.length];
  const lastName = fakeNames.lastNames[(hash * 7) % fakeNames.lastNames.length];

  return { firstName, lastName };
}

function capitalizeFirstLetter(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function getDisplayName(
  entityType: "student" | "teacher" | undefined,
  title?: string,
  firstName?: string,
  lastName?: string
) {
  if (entityType === "teacher") {
    if (title) {
      return `${title} ${lastName || firstName}`.trim();
    } else if (lastName) {
      return lastName.trim();
    } else if (firstName) {
      return firstName.trim();
    }
  } else if (firstName && firstName.length > 0) {
    if (lastName && lastName.length > 0) {
      //Create an abbreviated version of first name with last initial in the ideal case
      return `${firstName.trim()} ${lastName[0]}`;
    }

    return firstName.trim();
  } else if (lastName) {
    return lastName.trim();
  }

  Logger.logWarn("[getDisplayName] Could not generate a display name for player", { title, firstName, lastName });
  return DEFAULT_MISSING_PLAYER_NAME_INFO.displayName;
}
